import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Save, 
  Eye, 
  Building, 
  Phone, 
  MapPin, 
  Globe, 
  Plus, 
  Trash2,
  Image as ImageIcon,
  Edit3,
  BarChart3,
  Linkedin
} from 'lucide-react';
import { useApiMutation, useCompanySettings, useHeroStats } from '../hooks/useApi';

const CompanySettings = () => {
  const { data: companyData, loading: companyLoading, refetch: refetchCompany } = useCompanySettings();
  const { data: heroStatsData, loading: heroStatsLoading, refetch: refetchHeroStats } = useHeroStats();
  const { mutate, loading: saving } = useApiMutation();

  const [companyForm, setCompanyForm] = useState({
    name: '',
    tagline: '',
    description: '',
    hero_title: '',
    hero_subtitle: '',
    phone: '',
    office_locations: [],
    calendly_link: '',
    logo_url: '',
    logo_white_url: '',
    social_links: { linkedin: '' }
  });

  const [heroStats, setHeroStats] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('company');

  // Initialize form data
  useEffect(() => {
    if (companyData?.data) {
      const company = companyData.data;
      setCompanyForm({
        name: company.name || '',
        tagline: company.tagline || '',
        description: company.description || '',
        hero_title: company.hero_title || '',
        hero_subtitle: company.hero_subtitle || '',
        phone: company.phone || '',
        office_locations: company.office_locations || [],
        calendly_link: company.calendly_link || '',
        logo_url: company.logo_url || '',
        logo_white_url: company.logo_white_url || '',
        social_links: company.social_links || { linkedin: '' }
      });
    }
  }, [companyData]);

  useEffect(() => {
    if (heroStatsData?.data) {
      setHeroStats(heroStatsData.data.map(stat => ({
        id: stat.id,
        number: stat.number,
        label: stat.label,
        order: stat.order,
        is_active: stat.is_active
      })));
    }
  }, [heroStatsData]);

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    
    const result = await mutate('PUT', '/api/admin/company', companyForm);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Company settings updated successfully!' });
      refetchCompany();
    } else {
      setMessage({ type: 'error', text: result.error });
    }
  };

  const handleOfficeLocationChange = (index, value) => {
    const newLocations = [...companyForm.office_locations];
    newLocations[index] = value;
    setCompanyForm({ ...companyForm, office_locations: newLocations });
  };

  const addOfficeLocation = () => {
    setCompanyForm({
      ...companyForm,
      office_locations: [...companyForm.office_locations, '']
    });
  };

  const removeOfficeLocation = (index) => {
    const newLocations = companyForm.office_locations.filter((_, i) => i !== index);
    setCompanyForm({ ...companyForm, office_locations: newLocations });
  };

  const handleHeroStatChange = (index, field, value) => {
    const newStats = [...heroStats];
    newStats[index] = { ...newStats[index], [field]: value };
    setHeroStats(newStats);
  };

  const addHeroStat = () => {
    const newStat = {
      id: null,
      number: '',
      label: '',
      order: heroStats.length,
      is_active: true
    };
    setHeroStats([...heroStats, newStat]);
  };

  const removeHeroStat = async (index) => {
    const stat = heroStats[index];
    
    if (stat.id) {
      const result = await mutate('DELETE', `/api/admin/hero-stats/${stat.id}`);
      if (result.success) {
        setMessage({ type: 'success', text: 'Hero statistic deleted successfully!' });
        refetchHeroStats();
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } else {
      const newStats = heroStats.filter((_, i) => i !== index);
      setHeroStats(newStats);
    }
  };

  const saveHeroStats = async () => {
    for (const stat of heroStats) {
      if (stat.number && stat.label) {
        if (stat.id) {
          // Update existing
          await mutate('PUT', `/api/admin/hero-stats/${stat.id}`, {
            number: stat.number,
            label: stat.label,
            order: stat.order,
            is_active: stat.is_active
          });
        } else {
          // Create new
          await mutate('POST', '/api/admin/hero-stats', {
            number: stat.number,
            label: stat.label,
            order: stat.order,
            is_active: stat.is_active
          });
        }
      }
    }
    
    setMessage({ type: 'success', text: 'Hero statistics updated successfully!' });
    refetchHeroStats();
  };

  if (companyLoading || heroStatsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Company Settings</h1>
          <p className="text-gray-600 mt-1">Manage your company information and website hero section</p>
        </div>
        <Button 
          onClick={() => window.open('/', '_blank')}
          variant="outline"
          className="flex items-center space-x-2"
        >
          <Eye className="h-4 w-4" />
          <span>Preview Website</span>
        </Button>
      </div>

      {message.text && (
        <Alert className={`${message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
          <AlertDescription className={`${message.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="company" className="flex items-center space-x-2">
            <Building className="h-4 w-4" />
            <span>Company Info</span>
          </TabsTrigger>
          <TabsTrigger value="hero" className="flex items-center space-x-2">
            <Edit3 className="h-4 w-4" />
            <span>Hero Section</span>
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center space-x-2">
            <BarChart3 className="h-4 w-4" />
            <span>Statistics</span>
          </TabsTrigger>
        </TabsList>

        {/* Company Information Tab */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building className="h-5 w-5 text-blue-600" />
                <span>Company Information</span>
              </CardTitle>
              <CardDescription>
                Update your company's basic information displayed across the website
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCompanySubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Company Name</Label>
                    <Input
                      id="name"
                      value={companyForm.name}
                      onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                      placeholder="Blu Digital Technologies"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="phone"
                        value={companyForm.phone}
                        onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                        placeholder="855-937-3726"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="tagline">Tagline</Label>
                  <Input
                    id="tagline"
                    value={companyForm.tagline}
                    onChange={(e) => setCompanyForm({ ...companyForm, tagline: e.target.value })}
                    placeholder="Transforming Government & Enterprise Digital Infrastructure"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={companyForm.description}
                    onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })}
                    placeholder="Leading SaaS agency specializing in custom websites..."
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="logo_url">Logo URL (Color)</Label>
                    <div className="relative mt-1">
                      <ImageIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="logo_url"
                        value={companyForm.logo_url}
                        onChange={(e) => setCompanyForm({ ...companyForm, logo_url: e.target.value })}
                        placeholder="https://..."
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="logo_white_url">Logo URL (White)</Label>
                    <div className="relative mt-1">
                      <ImageIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="logo_white_url"
                        value={companyForm.logo_white_url}
                        onChange={(e) => setCompanyForm({ ...companyForm, logo_white_url: e.target.value })}
                        placeholder="https://..."
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                {/* Office Locations */}
                <div>
                  <Label>Office Locations</Label>
                  <div className="space-y-2 mt-2">
                    {companyForm.office_locations.map((location, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            value={location}
                            onChange={(e) => handleOfficeLocationChange(index, e.target.value)}
                            placeholder="Office location"
                            className="pl-10"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeOfficeLocation(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addOfficeLocation}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Office Location
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="calendly_link">Calendly Link</Label>
                  <div className="relative mt-1">
                    <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="calendly_link"
                      value={companyForm.calendly_link}
                      onChange={(e) => setCompanyForm({ ...companyForm, calendly_link: e.target.value })}
                      placeholder="https://calendly.com/..."
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="linkedin_url">LinkedIn Company URL</Label>
                  <div className="relative mt-1">
                    <Linkedin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="linkedin_url"
                      value={companyForm.social_links?.linkedin || ''}
                      onChange={(e) => setCompanyForm({ 
                        ...companyForm, 
                        social_links: { 
                          ...companyForm.social_links, 
                          linkedin: e.target.value 
                        } 
                      })}
                      placeholder="https://www.linkedin.com/company/blucreative"
                      className="pl-10"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    This will appear in your footer and enable LinkedIn sharing features
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Company Settings
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hero Section Tab */}
        <TabsContent value="hero">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Edit3 className="h-5 w-5 text-blue-600" />
                <span>Hero Section Content</span>
              </CardTitle>
              <CardDescription>
                Customize the main headline and subtitle that visitors see first
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCompanySubmit} className="space-y-6">
                <div>
                  <Label htmlFor="hero_title">Hero Title</Label>
                  <Input
                    id="hero_title"
                    value={companyForm.hero_title}
                    onChange={(e) => setCompanyForm({ ...companyForm, hero_title: e.target.value })}
                    placeholder="Building Digital Excellence"
                    className="mt-1 text-lg"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    This is the main headline visitors see first
                  </p>
                </div>

                <div>
                  <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
                  <Textarea
                    id="hero_subtitle"
                    value={companyForm.hero_subtitle}
                    onChange={(e) => setCompanyForm({ ...companyForm, hero_subtitle: e.target.value })}
                    placeholder="Custom web solutions, AI-powered tools, and accessible technology..."
                    rows={3}
                    className="mt-1"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Supporting text that describes your services
                  </p>
                </div>

                {/* Live Preview */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-dashed border-blue-200">
                  <h3 className="text-sm font-medium text-gray-600 mb-4">Live Preview</h3>
                  <div className="space-y-4">
                    <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                      <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-500 bg-clip-text text-transparent">
                        {companyForm.hero_title || 'Building Digital Excellence'}
                      </span>
                    </h1>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {companyForm.hero_subtitle || 'Custom web solutions, AI-powered tools, and accessible technology that transforms how organizations serve their communities.'}
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Hero Content
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hero Statistics Tab */}
        <TabsContent value="stats">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                <span>Hero Statistics</span>
              </CardTitle>
              <CardDescription>
                Add impressive statistics to display in your hero section
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {heroStats.map((stat, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-xl">
                    <div className="flex-1 grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`stat-number-${index}`}>Number/Value</Label>
                        <Input
                          id={`stat-number-${index}`}
                          value={stat.number}
                          onChange={(e) => handleHeroStatChange(index, 'number', e.target.value)}
                          placeholder="500+"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`stat-label-${index}`}>Label</Label>
                        <Input
                          id={`stat-label-${index}`}
                          value={stat.label}
                          onChange={(e) => handleHeroStatChange(index, 'label', e.target.value)}
                          placeholder="Projects Delivered"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeHeroStat(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addHeroStat}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Statistic
                </Button>

                {/* Statistics Preview */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-dashed border-blue-200">
                  <h3 className="text-sm font-medium text-gray-600 mb-4">Statistics Preview</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {heroStats.filter(stat => stat.number && stat.label).map((stat, index) => (
                      <div key={index} className="text-center">
                        <div className="text-2xl font-bold text-blue-600 mb-1">
                          {stat.number}
                        </div>
                        <div className="text-sm text-gray-600 font-medium">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={saveHeroStats}
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Statistics
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompanySettings;